import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  IconButton,
  Stack,
  Divider,
  Popover,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  InputAdornment
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { authGet, getServiceNowTickets, downloadServiceNowIncidentReport } from '../../services/apiService';
import {
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Speed as SpeedIcon,
  AccountTree as AccountTreeIcon,
  Whatshot as WhatshotIcon,
  Timeline as TimelineIcon,
  Insights as InsightsIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Assignment as AssignmentIcon,
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon,
  FiberManualRecord as DotIcon,
  HealthAndSafety as HealthIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Remove as FlatIcon,
  Gavel as RcaIcon
} from '@mui/icons-material';

/** Chart series */
const COLORS = ['#0d9488', '#2563eb', '#d97706', '#dc2626', '#7c3aed', '#db2777', '#059669', '#ea580c'];

const STATS_QUERY_DAYS = 90;
const TABLE_ROWS_OPTIONS = [25, 50, 100];

const MI = {
  forest: '#0f3d2e',
  forestMid: '#1D4434',
  teal: '#0d9488',
  paper: '#f6f8f7'
};

const rxExact = (s) => `^${String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`;

const ACTIVE_STATES = ['New', 'In Progress', 'Assigned', 'Active'];
const RESOLVED_STATES = ['Resolved', 'Closed', 'Cancelled'];

const getPriorityColor = (priority) => {
  const p = (priority || '').toLowerCase();
  if (p.includes('1') || p.includes('critical')) return { bg: '#ffebee', color: '#c62828', border: '#ef9a9a' };
  if (p.includes('2') || p.includes('high')) return { bg: '#fff3e0', color: '#e65100', border: '#ffcc80' };
  if (p.includes('3') || p.includes('medium')) return { bg: '#fff8e1', color: '#f57f17', border: '#ffe082' };
  return { bg: '#f3f4f6', color: '#4b5563', border: '#d1d5db' };
};

const getStateColor = (state) => {
  const s = (state || '').toLowerCase();
  if (s.includes('resolved') || s.includes('closed')) return { bg: '#e8f5e9', color: '#2e7d32' };
  if (s.includes('progress') || s.includes('active') || s.includes('assigned')) return { bg: '#e3f2fd', color: '#1565c0' };
  return { bg: '#fff3e0', color: '#e65100' };
};

const fmtHrs = (v) => (v == null || Number.isNaN(v) ? '—' : `${v} h`);

const fmtDuration = (openedAt, resolvedAt) => {
  const start = openedAt ? new Date(openedAt) : null;
  const end = resolvedAt ? new Date(resolvedAt) : new Date();
  if (!start) return '—';
  const hours = (end - start) / 3600000;
  if (hours < 24) return `${Math.round(hours)}h`;
  const days = Math.floor(hours / 24);
  const rem = Math.round(hours % 24);
  return rem > 0 ? `${days}d ${rem}h` : `${days}d`;
};

const bucketTrendData = (daily, granularity) => {
  if (!daily || daily.length === 0 || granularity === 'daily') return daily;
  const buckets = {};
  daily.forEach((item) => {
    const d = new Date(item.date + 'T00:00:00Z');
    let key;
    if (granularity === 'weekly') {
      const day = d.getUTCDay();
      const diff = day === 0 ? -6 : 1 - day;
      const monday = new Date(d);
      monday.setUTCDate(d.getUTCDate() + diff);
      key = monday.toISOString().split('T')[0];
    } else {
      key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
    }
    if (!buckets[key]) buckets[key] = { date: key, open: 0, resolved: 0, _openMax: 0 };
    buckets[key]._openMax = Math.max(buckets[key]._openMax, item.open);
    buckets[key].resolved += item.resolved;
  });
  return Object.values(buckets)
    .sort((a, b) => a.date.localeCompare(b.date))
    .map(({ _openMax, ...rest }) => ({ ...rest, open: _openMax }));
};

const exportToCsv = (incidents) => {
  const headers = ['Number', 'Short Description', 'State', 'Priority', 'Opened At', 'Resolved At', 'Duration', 'Service', 'Assigned To', 'Problem Number', 'Problem State', 'Has RCA'];
  const rows = incidents.map((i) => [
    i.number || '',
    `"${(i.shortDescription || '').replace(/"/g, '""')}"`,
    i.state || '',
    i.priority || '',
    i.openedAt ? new Date(i.openedAt).toLocaleString() : '',
    i.resolvedAt ? new Date(i.resolvedAt).toLocaleString() : '',
    fmtDuration(i.openedAt, i.resolvedAt),
    i.service || i.serviceTag || '',
    i.assignedTo || '',
    i.problemNumber || '',
    i.problemState || '',
    i.incidentReportAttachmentSysId ? 'Yes' : 'No'
  ]);
  const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `major-incidents-${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const IncidentTab = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [trends, setTrends] = useState(null);
  const [timeToResolve, setTimeToResolve] = useState(null);
  const [operational, setOperational] = useState(null);
  const [topServices, setTopServices] = useState([]);
  const [windowDays, setWindowDays] = useState(STATS_QUERY_DAYS);
  const [instanceUrl, setInstanceUrl] = useState('');
  const [metricsInfoAnchor, setMetricsInfoAnchor] = useState(null);

  // Chart drill-down modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalIncidents, setModalIncidents] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalNotice, setModalNotice] = useState(null);

  // Trend granularity toggle
  const [trendGranularity, setTrendGranularity] = useState('daily');

  // Persistent incidents table
  const [tableIncidents, setTableIncidents] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [tableTotal, setTableTotal] = useState(0);
  const [tablePage, setTablePage] = useState(0);
  const [tableRowsPerPage, setTableRowsPerPage] = useState(25);
  const [tableSearch, setTableSearch] = useState('');
  const [tablePriority, setTablePriority] = useState('');
  const [tableState, setTableState] = useState('');
  const [tableHasProblem, setTableHasProblem] = useState(false);
  const [tableHasRca, setTableHasRca] = useState(false);
  const [tableOrder, setTableOrder] = useState('desc');
  const [tableOrderBy, setTableOrderBy] = useState('openedAt');
  const [downloadingReportSysId, setDownloadingReportSysId] = useState(null);

  const searchDebounceRef = useRef(null);

  useEffect(() => {
    fetchIncidentStats();
    fetchServiceNowConfig();
  }, []);

  useEffect(() => {
    setTablePage(0);
  }, [tableSearch, tablePriority, tableState, tableHasProblem, tableHasRca]);

  useEffect(() => {
    fetchTableIncidents();
  }, [tablePage, tableRowsPerPage, tableSearch, tablePriority, tableState, tableHasProblem, tableHasRca]);

  const fetchServiceNowConfig = async () => {
    try {
      const response = await authGet('/api/servicenow/config');
      if (response.success && response.config?.instanceUrl) {
        setInstanceUrl(response.config.instanceUrl.replace(/\/+$/, ''));
      }
    } catch (err) {
      console.error('Error fetching ServiceNow config:', err);
    }
  };

  const fetchIncidentStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authGet(`/api/servicenow/tickets/stats?days=${STATS_QUERY_DAYS}`);
      if (response.success) {
        setStats(response.stats);
        setTrends(response.trends);
        setTimeToResolve(response.timeToResolve);
        setOperational(response.operational || null);
        setTopServices(response.topServices || []);
        if (response.windowDays != null) setWindowDays(response.windowDays);
      } else {
        setError(response.message || 'Failed to fetch incident statistics');
      }
    } catch (err) {
      console.error('Error fetching incident stats:', err);
      setError(err.response?.data?.message || 'Failed to fetch incident statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchTableIncidents = useCallback(async () => {
    try {
      setTableLoading(true);
      const filters = {
        page: tablePage + 1,
        limit: tableRowsPerPage
      };
      if (tableSearch) filters.search = tableSearch;
      if (tablePriority) filters.priority = rxExact(tablePriority);
      if (tableState) filters.state = tableState;
      if (tableHasProblem) filters.hasProblem = 'true';
      if (tableHasRca) filters.hasRca = 'true';
      const response = await getServiceNowTickets(filters);
      if (response.success) {
        setTableIncidents(response.tickets || []);
        setTableTotal(response.pagination?.total || 0);
      }
    } catch (err) {
      console.error('Error fetching table incidents:', err);
    } finally {
      setTableLoading(false);
    }
  }, [tablePage, tableRowsPerPage, tableSearch, tablePriority, tableState, tableHasProblem, tableHasRca]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => setTableSearch(val), 350);
  };

  const openIncidentListModal = async (title, query = {}, options = {}) => {
    const { clientFilter } = options;
    try {
      setModalOpen(true);
      setModalTitle(title);
      setModalIncidents([]);
      setModalNotice(null);
      setModalLoading(true);
      const response = await getServiceNowTickets({ page: 1, limit: 2000, ...query });
      if (response.success) {
        let incidents = response.tickets || [];
        if (typeof clientFilter === 'function') incidents = incidents.filter(clientFilter);
        setModalIncidents(incidents);
      } else {
        setModalNotice(response.message || 'Failed to fetch incidents');
      }
    } catch (err) {
      console.error('Error fetching incidents:', err);
      setModalNotice(err.response?.data?.message || 'Failed to fetch incidents');
    } finally {
      setModalLoading(false);
    }
  };

  const handleCardClick = (filterType, title) => {
    if (filterType === 'active') {
      return openIncidentListModal(title, {}, {
        clientFilter: (i) => {
          const s = (i.state || '').toLowerCase();
          return !s.includes('resolved') && !s.includes('closed');
        }
      });
    }
    if (filterType === 'resolved') return openIncidentListModal(title, { state: 'resolved|closed' });
    return openIncidentListModal(title, {});
  };

  const handleTrendPointClick = (seriesKey, dateStr) => {
    if (!dateStr) return;
    if (seriesKey === 'open') return openIncidentListModal(`Open (as of ${dateStr})`, { openOnDate: dateStr });
    if (seriesKey === 'resolved') return openIncidentListModal(`Resolved on ${dateStr}`, { resolvedOn: dateStr });
  };

  const handleStateBarClick = (stateName) => {
    if (!stateName) return;
    openIncidentListModal(`State: ${stateName}`, { state: rxExact(stateName) });
  };

  const handlePrioritySliceClick = (priorityName) => {
    if (!priorityName) return;
    openIncidentListModal(`Priority: ${priorityName}`, { priority: rxExact(priorityName) });
  };

  const handlePriorityTrendPointClick = (priorityName, dateStr) => {
    if (!dateStr || !priorityName) return;
    openIncidentListModal(`${priorityName} · opened ${dateStr}`, { openedOn: dateStr, priority: rxExact(priorityName) });
  };

  const handleTtrTrendPointClick = (dateStr) => {
    if (!dateStr) return;
    openIncidentListModal(`Resolved on ${dateStr} (avg TTR series)`, { resolvedOn: dateStr });
  };

  const handleServiceTagTrendPointClick = (tag, dateStr) => {
    if (!dateStr || !tag) return;
    openIncidentListModal(`${tag} · resolved ${dateStr}`, { resolvedOn: dateStr, serviceTag: tag });
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setModalTitle('');
    setModalIncidents([]);
    setModalNotice(null);
  };

  const getSnIncidentUrl = (sysId) => {
    if (!instanceUrl || !sysId) return null;
    return `${instanceUrl}/nav_to.do?uri=incident.do?sys_id=${sysId}`;
  };

  const getSnProblemUrl = (problemSysId) => {
    if (!instanceUrl || !problemSysId) return null;
    return `${instanceUrl}/nav_to.do?uri=problem.do?sys_id=${encodeURIComponent(problemSysId)}`;
  };

  const handleDownloadRca = async (incident, fromModal = false) => {
    if (!incident?.sysId) return;
    setDownloadingReportSysId(incident.sysId);
    if (fromModal) setModalNotice(null);
    try {
      const blob = await downloadServiceNowIncidentReport(incident.sysId, { refresh: true });
      const name = incident.incidentReportFileName || `Incident Report - ${incident.number || incident.sysId}.document`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      if (fromModal) setModalNotice(err.message || 'Could not download RCA. Confirm attachment exists in ServiceNow and run sync.');
    } finally {
      setDownloadingReportSysId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try { return new Date(dateString).toLocaleString(); } catch { return dateString; }
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' });
    } catch { return dateString; }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>;
  }

  const priorityData = stats?.byPriority
    ? Object.entries(stats.byPriority).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value)
    : [];

  const stateData = stats?.byState
    ? Object.entries(stats.byState).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value)
    : [];

  const resolutionRate = stats?.total > 0 ? Math.round((stats.resolved / stats.total) * 100) : 0;
  const problemLinkagePct = operational?.problemLinkagePct ?? 0;
  const rcaCoveragePct = stats?.total > 0 ? Math.round(((operational?.withRcaCount ?? 0) / stats.total) * 100) : 0;

  const trendDisplayData = bucketTrendData(trends?.daily || [], trendGranularity);

  const kpiPaperClick = {
    cursor: 'pointer',
    transition: 'box-shadow 0.18s ease, transform 0.18s ease',
    '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' }
  };

  // ── Overall health ─────────────────────────────────────────────
  const p1Count = operational?.p1ActiveCount ?? 0;
  const mttrMedian = operational?.mttrHoursMedian ?? 0;
  const highPriActive = operational?.highPriorityActiveCount ?? 0;
  const age = operational?.ageBreakdown ?? { lt4h: 0, h4to24: 0, d1to7: 0, gt7d: 0 };
  const overallHealth =
    p1Count > 0 || mttrMedian > 48 || resolutionRate < 70 || age.gt7d > 2 ? 'critical'
    : highPriActive > 3 || mttrMedian > 24 || resolutionRate < 85 || age.d1to7 > 5 ? 'risk'
    : 'healthy';

  const HEALTH_CFG = {
    healthy: { label: 'Healthy', color: 'success.main', border: 'success.light', bg: '#f1f8e9', chip: 'success', icon: <CheckCircleIcon /> },
    risk:    { label: 'At Risk', color: 'warning.main', border: 'warning.light', bg: '#fff8e1', chip: 'warning', icon: <WarningIcon /> },
    critical:{ label: 'Critical', color: 'error.main',  border: 'error.light',  bg: '#fff5f5', chip: 'error',   icon: <ErrorIcon /> }
  };
  const health = HEALTH_CFG[overallHealth];

  // ── Period-over-period trend helpers ───────────────────────────
  const prev = operational?.prevPeriod ?? {};

  const mkTrend = (cur, prv, lowerIsBetter = false) => {
    if (cur == null || prv == null || prv === 0) return null;
    const delta = cur - prv;
    if (Math.abs(delta) < 0.01) return { dir: 'flat', delta: 0, pct: 0 };
    const pct = Math.abs(Math.round((delta / prv) * 100));
    const improved = lowerIsBetter ? delta < 0 : delta > 0;
    return { dir: delta > 0 ? 'up' : 'down', delta, pct, improved };
  };

  const TrendBadge = ({ trend, size = 'small' }) => {
    if (!trend) return null;
    const isFlat = trend.dir === 'flat';
    const color = isFlat ? 'text.secondary' : trend.improved ? 'success.main' : 'error.main';
    const Icon = isFlat ? FlatIcon : trend.dir === 'up' ? ArrowUpIcon : ArrowDownIcon;
    return (
      <Stack direction="row" alignItems="center" spacing={0.25} sx={{ display: 'inline-flex' }}>
        <Icon sx={{ fontSize: size === 'small' ? 12 : 14, color }} />
        <Typography variant="caption" sx={{ fontSize: '0.68rem', color, fontWeight: 600 }}>
          {isFlat ? 'No change' : `${trend.pct}% vs prev`}
        </Typography>
      </Stack>
    );
  };

  // RAG dot
  const RagDot = ({ status }) => {
    const colors = { green: '#4caf50', amber: '#ff9800', red: '#f44336', blue: '#2196f3' };
    return <Box component="span" sx={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', bgcolor: colors[status] || colors.blue, mr: 0.75, flexShrink: 0 }} />;
  };

  // KPI RAG status
  const p1Rag = p1Count === 0 ? 'green' : 'red';
  const mttrRag = mttrMedian === 0 ? 'blue' : mttrMedian < 24 ? 'green' : mttrMedian < 48 ? 'amber' : 'red';
  const linkRag = problemLinkagePct >= 50 ? 'green' : problemLinkagePct >= 20 ? 'amber' : 'red';
  const resRag = resolutionRate >= 90 ? 'green' : resolutionRate >= 75 ? 'amber' : 'red';
  const hpRag = highPriActive === 0 ? 'green' : highPriActive <= 3 ? 'amber' : 'red';
  const rcaRag = rcaCoveragePct >= 50 ? 'green' : rcaCoveragePct >= 25 ? 'amber' : 'red';

  const p1Trend = mkTrend(p1Count, prev.highPriorityActiveCount, true);
  const mttrTrend = mkTrend(mttrMedian, prev.mttrHoursMedian, true);
  const resTrend = mkTrend(resolutionRate, prev.resolutionRate, false);
  const hpTrend = mkTrend(highPriActive, prev.highPriorityActiveCount, true);

  // Summary sentence for health banner
  const healthSummary = (() => {
    const parts = [];
    if (p1Count > 0) parts.push(`${p1Count} P1 open`);
    if (highPriActive > 0) parts.push(`${highPriActive} high-priority active`);
    if (mttrMedian > 0) parts.push(`MTTR ${mttrMedian}h`);
    if (age.gt7d > 0) parts.push(`${age.gt7d} incident${age.gt7d > 1 ? 's' : ''} open > 7 days`);
    if (parts.length === 0) parts.push('All key indicators within target');
    return parts.join(' · ');
  })();

  // Active incident total from age breakdown
  const totalActive = age.lt4h + age.h4to24 + age.d1to7 + age.gt7d;

  return (
    <Box>

        {/* Info popover trigger */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <IconButton
            size="small"
            onClick={(e) => setMetricsInfoAnchor(e.currentTarget)}
            sx={{ color: MI.forestMid, bgcolor: alpha(MI.forest, 0.08), '&:hover': { bgcolor: alpha(MI.forest, 0.14) } }}
          >
            <InfoIcon fontSize="small" />
          </IconButton>
          <Typography
            component="button"
            type="button"
            variant="body2"
            onClick={(e) => setMetricsInfoAnchor(e.currentTarget)}
            sx={{
              m: 0, p: 0, border: 'none', background: 'none', cursor: 'pointer', font: 'inherit',
              color: 'text.primary', fontWeight: 600, textAlign: 'left',
              textDecoration: 'underline', textDecorationColor: alpha(MI.forest, 0.35),
              '&:hover': { color: MI.forestMid }
            }}
          >
            What these metrics represent
          </Typography>
        </Stack>

        <Popover
          open={Boolean(metricsInfoAnchor)}
          anchorEl={metricsInfoAnchor}
          onClose={() => setMetricsInfoAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{ paper: { sx: { maxWidth: 460, mt: 0.5, borderRadius: 2 } } }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>What these metrics represent</Typography>
            <Typography variant="body2" component="div" color="text.secondary" sx={{ '& strong': { color: 'text.primary' } }}>
              <strong>P1 / Critical open</strong> — active incidents with priority "1 - Critical" or equivalent.<br />
              <strong>MTTR</strong> — mean/median hours from opened → resolved for incidents closed in the {windowDays}-day window.<br />
              <strong>Problem linkage</strong> — share of incidents linked to a Problem record (root-cause track).<br />
              <strong>Resolution rate</strong> — share of all synced incidents in a resolved/closed state.<br />
              <strong>RCAs available</strong> — incidents with an "Incident Report" attachment in ServiceNow.<br />
              <strong>High-priority active</strong> — open incidents matching Critical/High/P1–P4 text pattern.<br />
              <strong>Note:</strong> Data reflects incidents synced after the ServiceNow migration.
            </Typography>
          </Box>
        </Popover>

        {/* Header banner */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.75, md: 2.25 },
            mb: 2.5,
            borderRadius: 3,
            position: 'relative',
            overflow: 'hidden',
            background: `linear-gradient(125deg, ${MI.forest} 0%, ${MI.forestMid} 42%, ${alpha(MI.teal, 0.85)} 100%)`,
            color: 'common.white',
            boxShadow: '0 8px 28px rgba(15, 61, 46, 0.2)'
          }}
        >
          <Box sx={{ position: 'absolute', right: -36, top: -36, width: 140, height: 140, borderRadius: '50%', background: alpha('#fff', 0.06) }} />
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ position: 'relative' }}>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5, opacity: 0.92 }}>
                <InsightsIcon sx={{ fontSize: 18 }} />
                <Typography variant="overline" sx={{ letterSpacing: '0.12em', fontWeight: 700, fontSize: '0.65rem' }}>
                  ServiceNow · Major incidents
                </Typography>
              </Stack>
              <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: -0.4, lineHeight: 1.25 }}>
                Incident health & recovery
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              <Chip size="small" label={`${windowDays}-day window`} sx={{ bgcolor: alpha('#fff', 0.15), color: 'common.white', fontWeight: 600, height: 26 }} />
              <Chip size="small" label="Click KPIs for lists" sx={{ bgcolor: alpha('#fff', 0.1), color: 'common.white', height: 26 }} />
              <Tooltip title="Refresh statistics">
                <IconButton size="small" onClick={fetchIncidentStats} sx={{ color: 'common.white', bgcolor: alpha('#fff', 0.1), '&:hover': { bgcolor: alpha('#fff', 0.2) } }}>
                  <RefreshIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Paper>

        {/* ─── Overall Health Status ────────────────────────────────────── */}
        <Paper
          variant="outlined"
          sx={{
            mb: 2.5,
            p: { xs: 1.75, sm: 2.25 },
            borderRadius: 2,
            borderWidth: 2,
            borderColor: health.border,
            bgcolor: health.bg,
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box sx={{ position: 'absolute', right: -20, top: -20, width: 100, height: 100, borderRadius: '50%', bgcolor: alpha('#000', 0.025) }} />
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1.5}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box sx={{ color: health.color, display: 'flex', alignItems: 'center' }}>
                <HealthIcon sx={{ fontSize: 36 }} />
              </Box>
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
                  <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.1em', color: 'text.secondary', fontSize: '0.65rem' }}>
                    Overall Incident Health
                  </Typography>
                  <Chip label={health.label} color={health.chip} size="small" sx={{ height: 20, fontWeight: 700, fontSize: '0.68rem' }} />
                </Stack>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                  {healthSummary}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap sx={{ flexShrink: 0 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h5" fontWeight={800} color={health.color} lineHeight={1}>{stats?.active ?? 0}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Open</Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h5" fontWeight={800} color="text.primary" lineHeight={1}>{stats?.total ?? 0}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Total</Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h5" fontWeight={800} color={MI.forestMid} lineHeight={1}>{windowDays}d</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Window</Typography>
              </Box>
            </Stack>
          </Stack>
        </Paper>

        {/* ─── Leadership KPI Grid ──────────────────────────────────────── */}
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: '0.08em', display: 'block', mb: 1.25 }}>
            Leadership scorecard · Click any tile to drill into incidents
          </Typography>
          <Grid container spacing={1.5}>

            {/* P1 / Critical open */}
            <Grid item xs={6} sm={4} md={2}>
              <Paper variant="outlined" onClick={() => openIncidentListModal('P1 / Critical Open', {}, { clientFilter: (i) => { const p = (i.priority||'').toLowerCase(); const s = (i.state||'').toLowerCase(); return !s.includes('resolved')&&!s.includes('closed')&&!s.includes('cancelled')&&(p.includes('1')||p.includes('critical')); } })}
                sx={{ p: 2, height: '100%', borderRadius: 2, borderLeftWidth: 4, borderLeftColor: p1Count > 0 ? 'error.main' : 'success.main', ...kpiPaperClick, bgcolor: p1Count > 0 ? '#fff5f5' : '#f9fbe7' }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Stack direction="row" alignItems="center">
                    <RagDot status={p1Rag} />
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>P1 / Critical open</Typography>
                  </Stack>
                  <ErrorIcon sx={{ fontSize: 16, color: p1Count > 0 ? 'error.main' : 'success.main', opacity: 0.7 }} />
                </Stack>
                <Typography variant="h3" fontWeight={800} color={p1Count > 0 ? 'error.main' : 'success.main'} lineHeight={1} sx={{ mb: 0.5 }}>
                  {p1Count}
                </Typography>
                {p1Count > 0 && <Chip label="Action required" color="error" size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, mb: 0.75 }} />}
                <TrendBadge trend={p1Trend} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.62rem' }}>Target: 0 open</Typography>
              </Paper>
            </Grid>

            {/* MTTR Median */}
            <Grid item xs={6} sm={4} md={2}>
              <Paper variant="outlined"
                sx={{ p: 2, height: '100%', borderRadius: 2, borderLeftWidth: 4, borderLeftColor: mttrRag === 'green' ? 'success.main' : mttrRag === 'amber' ? 'warning.main' : mttrRag === 'red' ? 'error.main' : 'info.main' }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Stack direction="row" alignItems="center">
                    <RagDot status={mttrRag} />
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>MTTR (median)</Typography>
                  </Stack>
                  <SpeedIcon sx={{ fontSize: 16, color: 'text.secondary', opacity: 0.7 }} />
                </Stack>
                <Typography variant="h3" fontWeight={800} color={mttrRag === 'green' ? 'success.main' : mttrRag === 'amber' ? 'warning.main' : mttrRag === 'red' ? 'error.main' : 'text.primary'} lineHeight={1} sx={{ mb: 0.5 }}>
                  {fmtHrs(mttrMedian)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '0.65rem' }}>Mean: {fmtHrs(operational?.mttrHoursAvg)} · n={operational?.mttrResolvedCount ?? 0}</Typography>
                <TrendBadge trend={mttrTrend} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.62rem' }}>Target: &lt; 24h</Typography>
              </Paper>
            </Grid>

            {/* Problem Linkage */}
            <Grid item xs={6} sm={4} md={2}>
              <Paper variant="outlined" onClick={() => openIncidentListModal('Problem-Linked Incidents', { hasProblem: 'true' })}
                sx={{ p: 2, height: '100%', borderRadius: 2, borderLeftWidth: 4, borderLeftColor: linkRag === 'green' ? 'success.main' : linkRag === 'amber' ? 'warning.main' : 'error.main', ...kpiPaperClick }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Stack direction="row" alignItems="center">
                    <RagDot status={linkRag} />
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Problem linkage</Typography>
                  </Stack>
                  <AccountTreeIcon sx={{ fontSize: 16, color: 'text.secondary', opacity: 0.7 }} />
                </Stack>
                <Typography variant="h3" fontWeight={800} color={linkRag === 'green' ? 'success.main' : linkRag === 'amber' ? 'warning.main' : 'error.main'} lineHeight={1} sx={{ mb: 0.5 }}>
                  {problemLinkagePct}%
                </Typography>
                <LinearProgress variant="determinate" value={Math.min(problemLinkagePct, 100)}
                  color={linkRag === 'green' ? 'success' : linkRag === 'amber' ? 'warning' : 'error'}
                  sx={{ height: 5, borderRadius: 3, mb: 0.75 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem' }}>{operational?.problemLinkedCount ?? 0} of {stats?.total ?? 0} linked</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.62rem' }}>Target: &gt; 50%</Typography>
              </Paper>
            </Grid>

            {/* Resolution Rate */}
            <Grid item xs={6} sm={4} md={2}>
              <Paper variant="outlined" onClick={() => handleCardClick('resolved', 'Resolved Incidents')}
                sx={{ p: 2, height: '100%', borderRadius: 2, borderLeftWidth: 4, borderLeftColor: resRag === 'green' ? 'success.main' : resRag === 'amber' ? 'warning.main' : 'error.main', ...kpiPaperClick }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Stack direction="row" alignItems="center">
                    <RagDot status={resRag} />
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Resolution rate</Typography>
                  </Stack>
                  <CheckCircleIcon sx={{ fontSize: 16, color: 'text.secondary', opacity: 0.7 }} />
                </Stack>
                <Typography variant="h3" fontWeight={800} color={resRag === 'green' ? 'success.main' : resRag === 'amber' ? 'warning.main' : 'error.main'} lineHeight={1} sx={{ mb: 0.5 }}>
                  {resolutionRate}%
                </Typography>
                <LinearProgress variant="determinate" value={resolutionRate}
                  color={resRag === 'green' ? 'success' : resRag === 'amber' ? 'warning' : 'error'}
                  sx={{ height: 5, borderRadius: 3, mb: 0.75 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem' }}>{stats?.resolved ?? 0} resolved of {stats?.total ?? 0}</Typography>
                <TrendBadge trend={resTrend} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.62rem' }}>Target: &gt; 90%</Typography>
              </Paper>
            </Grid>

            {/* High Priority Active */}
            <Grid item xs={6} sm={4} md={2}>
              <Paper variant="outlined" onClick={() => handleCardClick('active', 'Active Incidents')}
                sx={{ p: 2, height: '100%', borderRadius: 2, borderLeftWidth: 4, borderLeftColor: hpRag === 'green' ? 'success.main' : hpRag === 'amber' ? 'warning.main' : 'error.main', ...kpiPaperClick }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Stack direction="row" alignItems="center">
                    <RagDot status={hpRag} />
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>High-pri active</Typography>
                  </Stack>
                  <WhatshotIcon sx={{ fontSize: 16, color: 'text.secondary', opacity: 0.7 }} />
                </Stack>
                <Typography variant="h3" fontWeight={800} color={hpRag === 'green' ? 'success.main' : hpRag === 'amber' ? 'warning.main' : 'error.main'} lineHeight={1} sx={{ mb: 0.5 }}>
                  {highPriActive}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '0.65rem' }}>P1–P4 / Critical–High open</Typography>
                <TrendBadge trend={hpTrend} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.62rem' }}>Target: 0</Typography>
              </Paper>
            </Grid>

            {/* RCA Coverage */}
            <Grid item xs={6} sm={4} md={2}>
              <Paper variant="outlined" onClick={() => openIncidentListModal('Incidents with RCA', { hasRca: 'true' })}
                sx={{ p: 2, height: '100%', borderRadius: 2, borderLeftWidth: 4, borderLeftColor: rcaRag === 'green' ? 'success.main' : rcaRag === 'amber' ? 'warning.main' : 'error.main', ...kpiPaperClick }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Stack direction="row" alignItems="center">
                    <RagDot status={rcaRag} />
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>RCA coverage</Typography>
                  </Stack>
                  <RcaIcon sx={{ fontSize: 16, color: 'text.secondary', opacity: 0.7 }} />
                </Stack>
                <Typography variant="h3" fontWeight={800} color={rcaRag === 'green' ? 'success.main' : rcaRag === 'amber' ? 'warning.main' : 'error.main'} lineHeight={1} sx={{ mb: 0.5 }}>
                  {rcaCoveragePct}%
                </Typography>
                <LinearProgress variant="determinate" value={Math.min(rcaCoveragePct, 100)}
                  color={rcaRag === 'green' ? 'success' : rcaRag === 'amber' ? 'warning' : 'error'}
                  sx={{ height: 5, borderRadius: 3, mb: 0.75 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem' }}>{operational?.withRcaCount ?? 0} reports attached</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.62rem' }}>Target: &gt; 50%</Typography>
              </Paper>
            </Grid>

          </Grid>
        </Box>

        <Divider sx={{ mb: 2.5 }} />

        {/* ─── Operational KPI strip ────────────────────────────────────── */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: { xs: 'none', lg: 'grid' }, gridTemplateColumns: 'repeat(8, minmax(0, 1fr))', gap: 1, mb: 0.75, alignItems: 'end' }}>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: '0.08em', gridColumn: 'span 4' }}>Volume & status</Typography>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: '0.08em', gridColumn: 'span 4' }}>Recovery & problem management</Typography>
          </Box>
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: '0.08em', display: { xs: 'block', lg: 'none' }, mb: 0.75 }}>Volume & status</Typography>
          <Box
            sx={{
              display: 'grid',
              gap: 1,
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))', lg: 'repeat(8, minmax(0, 1fr))' },
              alignItems: 'stretch'
            }}
          >
            {/* Total */}
            <Paper elevation={1} onClick={() => handleCardClick('total', 'All Incidents')}
              sx={{ p: 1, py: 1.15, position: 'relative', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)', border: '1px solid', borderColor: '#90caf9', ...kpiPaperClick }}
            >
              <Tooltip title="All incidents stored in the portal from ServiceNow sync. Click to list." arrow placement="top">
                <IconButton size="small" sx={{ position: 'absolute', right: 2, top: 2, p: 0.2 }} onClick={(e) => e.stopPropagation()}>
                  <InfoIcon sx={{ fontSize: 16 }} color="action" />
                </IconButton>
              </Tooltip>
              <Typography color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.65rem', mb: 0.25, lineHeight: 1.2 }}>Total incidents</Typography>
              <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', color: '#1976d2', lineHeight: 1.15, mb: 0.25 }}>{stats?.total || 0}</Typography>
              <Typography color="text.secondary" sx={{ fontSize: '0.62rem', lineHeight: 1.2 }}>Synced catalog</Typography>
            </Paper>

            {/* Active */}
            <Paper elevation={1} onClick={() => handleCardClick('active', 'Active Incidents')}
              sx={{ p: 1, py: 1.15, position: 'relative', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)', border: '1px solid', borderColor: '#ffcc80', ...kpiPaperClick }}
            >
              <Tooltip title="Open backlog: states other than resolved/closed. Click to list." arrow placement="top">
                <IconButton size="small" sx={{ position: 'absolute', right: 2, top: 2, p: 0.2 }} onClick={(e) => e.stopPropagation()}>
                  <InfoIcon sx={{ fontSize: 16 }} color="action" />
                </IconButton>
              </Tooltip>
              <Typography color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.65rem', mb: 0.25, lineHeight: 1.2 }}>Active (backlog)</Typography>
              <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', color: '#e65100', lineHeight: 1.15, mb: 0.25 }}>{stats?.active || 0}</Typography>
              <Typography color="text.secondary" sx={{ fontSize: '0.62rem', lineHeight: 1.2 }}>Not resolved/closed</Typography>
            </Paper>

            {/* Resolved */}
            <Paper elevation={1} onClick={() => handleCardClick('resolved', 'Resolved Incidents')}
              sx={{ p: 1, py: 1.15, position: 'relative', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)', border: '1px solid', borderColor: '#a5d6a7', ...kpiPaperClick }}
            >
              <Tooltip title="Incidents in resolved or closed state. Click to list." arrow placement="top">
                <IconButton size="small" sx={{ position: 'absolute', right: 2, top: 2, p: 0.2 }} onClick={(e) => e.stopPropagation()}>
                  <InfoIcon sx={{ fontSize: 16 }} color="action" />
                </IconButton>
              </Tooltip>
              <Typography color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.65rem', mb: 0.25, lineHeight: 1.2 }}>Resolved</Typography>
              <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', color: '#388e3c', lineHeight: 1.15, mb: 0.25 }}>{stats?.resolved || 0}</Typography>
              <Typography color="text.secondary" sx={{ fontSize: '0.62rem', lineHeight: 1.2 }}>Closed volume</Typography>
            </Paper>

            {/* Resolution rate */}
            <Paper elevation={1} onClick={() => handleCardClick('total', 'All Incidents')}
              sx={{ p: 1, py: 1.15, position: 'relative', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', background: 'linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%)', border: '1px solid', borderColor: '#80cbc4', ...kpiPaperClick }}
            >
              <Tooltip title="Share of all synced incidents that reached a resolved/closed state." arrow placement="top">
                <IconButton size="small" sx={{ position: 'absolute', right: 2, top: 2, p: 0.2 }} onClick={(e) => e.stopPropagation()}>
                  <InfoIcon sx={{ fontSize: 16 }} color="action" />
                </IconButton>
              </Tooltip>
              <Typography color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.65rem', mb: 0.25, lineHeight: 1.2 }}>Resolution rate</Typography>
              <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', color: '#00695c', lineHeight: 1.15, mb: 0.25 }}>{resolutionRate}%</Typography>
              <Typography color="text.secondary" sx={{ fontSize: '0.62rem', lineHeight: 1.2 }}>Of synced incidents</Typography>
            </Paper>

            <Box sx={{ gridColumn: { xs: '1 / -1', lg: 'auto' }, display: { xs: 'block', lg: 'none' }, py: 0.25 }}>
              <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: '0.08em' }}>Recovery & problem management</Typography>
            </Box>

            {/* MTTR mean */}
            <Paper elevation={1}
              sx={{ p: 1, py: 1.15, position: 'relative', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', background: 'linear-gradient(135deg, #e1f5fe 0%, #b3e5fc 100%)', border: '1px solid', borderColor: '#4fc3f7' }}
            >
              <Tooltip title="Mean hours from opened to resolved for incidents closed in the rolling window." arrow placement="top">
                <IconButton size="small" sx={{ position: 'absolute', right: 2, top: 2, p: 0.2 }}>
                  <InfoIcon sx={{ fontSize: 16 }} color="action" />
                </IconButton>
              </Tooltip>
              <Typography color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.65rem', mb: 0.25, lineHeight: 1.2 }}>MTTR (mean)</Typography>
              <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', color: '#0277bd', lineHeight: 1.15, mb: 0.25 }}>{fmtHrs(operational?.mttrHoursAvg)}</Typography>
              <Typography color="text.secondary" sx={{ fontSize: '0.62rem', lineHeight: 1.2 }}>{windowDays}d · n={operational?.mttrResolvedCount ?? 0}</Typography>
            </Paper>

            {/* MTTR median */}
            <Paper elevation={1}
              sx={{ p: 1, py: 1.15, position: 'relative', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)', border: '1px solid', borderColor: '#a5d6a7' }}
            >
              <Tooltip title="Median restore time — less sensitive to outliers than the mean." arrow placement="top">
                <IconButton size="small" sx={{ position: 'absolute', right: 2, top: 2, p: 0.2 }}>
                  <InfoIcon sx={{ fontSize: 16 }} color="action" />
                </IconButton>
              </Tooltip>
              <Typography color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.65rem', mb: 0.25, lineHeight: 1.2 }}>MTTR (median)</Typography>
              <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', color: '#2e7d32', lineHeight: 1.15, mb: 0.25 }}>{fmtHrs(operational?.mttrHoursMedian)}</Typography>
              <Typography color="text.secondary" sx={{ fontSize: '0.62rem', lineHeight: 1.2 }}>Typical restore</Typography>
            </Paper>

            {/* Problem linkage */}
            <Paper elevation={1}
              sx={{ p: 1, py: 1.15, position: 'relative', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)', border: '1px solid', borderColor: '#ce93d8' }}
            >
              <Tooltip title="Percentage of incidents linked to a Problem record in ServiceNow." arrow placement="top">
                <IconButton size="small" sx={{ position: 'absolute', right: 2, top: 2, p: 0.2 }}>
                  <InfoIcon sx={{ fontSize: 16 }} color="action" />
                </IconButton>
              </Tooltip>
              <Typography color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.65rem', mb: 0.25, lineHeight: 1.2 }}>Problem linkage</Typography>
              <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', color: '#7b1fa2', lineHeight: 1.15, mb: 0.25 }}>
                {operational?.problemLinkagePct != null ? `${operational.problemLinkagePct}%` : '—'}
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: '0.62rem', lineHeight: 1.2 }}>{operational?.problemLinkedCount ?? 0} PRB</Typography>
            </Paper>

            {/* High-priority active */}
            <Paper elevation={1}
              sx={{ p: 1, py: 1.15, position: 'relative', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)', border: '1px solid', borderColor: '#ef9a9a' }}
            >
              <Tooltip title="Open incidents matching Critical/High / P1–P4 style priority text." arrow placement="top">
                <IconButton size="small" sx={{ position: 'absolute', right: 2, top: 2, p: 0.2 }}>
                  <InfoIcon sx={{ fontSize: 16 }} color="action" />
                </IconButton>
              </Tooltip>
              <Typography color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.65rem', mb: 0.25, lineHeight: 1.2 }}>High-priority active</Typography>
              <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', color: '#c62828', lineHeight: 1.15, mb: 0.25 }}>{operational?.highPriorityActiveCount ?? '—'}</Typography>
              <Typography color="text.secondary" sx={{ fontSize: '0.62rem', lineHeight: 1.2 }}>Risk signal</Typography>
            </Paper>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* ─── Trends & distribution ────────────────────────────────────── */}
        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: '0.08em', display: 'block', mb: 1.5 }}>
          Trends & distribution
        </Typography>

        {/* ── Row 1: Open vs Resolved + Priority donut ──────────────────── */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '7fr 5fr' }, gap: 2, mb: 2 }}>
          {/* Open vs Resolved trend */}
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5, gap: 1, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: MI.forestMid }}>Open vs resolved trend</Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Last {windowDays} days · click a point to list incidents
                </Typography>
              </Box>
              <Stack direction="row" spacing={0.5} alignItems="center">
                {['daily', 'weekly', 'monthly'].map((g) => (
                  <Button
                    key={g}
                    size="small"
                    variant={trendGranularity === g ? 'contained' : 'outlined'}
                    onClick={() => setTrendGranularity(g)}
                    sx={{
                      textTransform: 'capitalize',
                      minWidth: 60,
                      height: 26,
                      fontSize: '0.72rem',
                      ...(trendGranularity === g ? { bgcolor: MI.forestMid } : {})
                    }}
                  >
                    {g}
                  </Button>
                ))}
                <Tooltip title={`Open = backlog snapshot at ${trendGranularity === 'daily' ? 'day' : trendGranularity === 'weekly' ? 'week' : 'month'} end; Resolved = count closed in period.`} arrow placement="left">
                  <IconButton size="small"><InfoIcon fontSize="small" color="action" /></IconButton>
                </Tooltip>
              </Stack>
            </Box>
            <Box sx={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendDisplayData} margin={{ top: 5, right: 20, left: 0, bottom: 55 }}>
                  <defs>
                    <linearGradient id="gradOpen" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ff9800" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#ff9800" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="gradResolved" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#4caf50" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#4caf50" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" angle={-45} textAnchor="end" height={65} tick={{ fontSize: 9 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11 }} width={32} />
                  <RechartsTooltip contentStyle={{ borderRadius: 8, fontSize: '0.8rem' }} />
                  <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '6px' }} />
                  <Area type="monotone" dataKey="open" stroke="#ff9800" strokeWidth={2} fill="url(#gradOpen)" name="Open"
                    dot={(props) => {
                      const { cx, cy, payload, dataKey } = props;
                      if (cx == null || cy == null) return null;
                      return <circle cx={cx} cy={cy} r={4} fill="#ff9800" stroke="#fff" strokeWidth={1} style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); handleTrendPointClick(dataKey, payload?.date); }} />;
                    }}
                    activeDot={{ r: 6 }}
                  />
                  <Area type="monotone" dataKey="resolved" stroke="#4caf50" strokeWidth={2} fill="url(#gradResolved)" name="Resolved"
                    dot={(props) => {
                      const { cx, cy, payload, dataKey } = props;
                      if (cx == null || cy == null) return null;
                      return <circle cx={cx} cy={cy} r={4} fill="#4caf50" stroke="#fff" strokeWidth={1} style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); handleTrendPointClick(dataKey, payload?.date); }} />;
                    }}
                    activeDot={{ r: 6 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

          {/* Priority distribution donut */}
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: MI.forestMid }}>Priority distribution</Typography>
                <Typography variant="caption" color="text.secondary" display="block">Click a slice to list incidents</Typography>
              </Box>
              <Tooltip title="Share of incidents by ServiceNow priority field." arrow placement="left">
                <IconButton size="small"><InfoIcon fontSize="small" color="action" /></IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={priorityData} cx="50%" cy="45%" labelLine={false}
                    label={({ name, percent }) => percent > 0.04 ? `${(percent * 100).toFixed(0)}%` : ''}
                    outerRadius={90} innerRadius={30} dataKey="value" style={{ cursor: 'pointer' }}
                    onClick={(sliceData) => { const name = sliceData?.name ?? sliceData?.payload?.name; if (name) handlePrioritySliceClick(name); }}
                  >
                    {priorityData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                  </Pie>
                  <RechartsTooltip formatter={(value, name) => [value, name]} contentStyle={{ borderRadius: 8, fontSize: '0.8rem' }} />
                  <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '6px' }} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Box>

        {/* ── Row 2: State distribution + Monthly volume ────────────────── */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
          {/* State distribution */}
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: MI.forestMid }}>State distribution</Typography>
                <Typography variant="caption" color="text.secondary" display="block">Click a bar to list incidents in that state</Typography>
              </Box>
              <Tooltip title="Count of incidents by ServiceNow state." arrow placement="left">
                <IconButton size="small"><InfoIcon fontSize="small" color="action" /></IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stateData} margin={{ top: 5, right: 20, left: 0, bottom: 55 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={65} tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} width={32} />
                  <RechartsTooltip contentStyle={{ borderRadius: 8, fontSize: '0.8rem' }} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]} cursor="pointer"
                    onClick={(data) => { if (data?.name != null) handleStateBarClick(data.name); }}
                  >
                    {stateData.map((entry, index) => (
                      <Cell key={`state-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

          {/* Monthly volume */}
          {trends?.monthly && trends.monthly.length > 0 ? (
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: MI.forestMid }}>Monthly volume (12 months)</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">Incidents opened per month — click a bar to list</Typography>
                </Box>
                <Tooltip title="Count of incidents opened in each calendar month over the last 12 months." arrow placement="left">
                  <IconButton size="small"><InfoIcon fontSize="small" color="action" /></IconButton>
                </Tooltip>
              </Box>
              <Box sx={{ height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={trends.monthly} margin={{ top: 5, right: 20, left: 0, bottom: 55 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" angle={-45} textAnchor="end" height={65} tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 11 }} width={32} />
                    <RechartsTooltip formatter={(value, name) => [value, name === 'opened' ? 'Opened' : 'Resolved']} contentStyle={{ borderRadius: 8, fontSize: '0.8rem' }} />
                    <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '6px' }} formatter={(v) => v === 'opened' ? 'Opened' : 'Resolved'} />
                    <Bar dataKey="opened" fill={MI.teal} radius={[4, 4, 0, 0]} name="opened" cursor="pointer"
                      onClick={(data) => {
                        if (data?.month) {
                          const [y, m] = data.month.split('-');
                          const start = `${y}-${m}-01`;
                          const lastDay = new Date(parseInt(y), parseInt(m), 0).getDate();
                          const end = `${y}-${m}-${String(lastDay).padStart(2, '0')}`;
                          openIncidentListModal(`Opened in ${data.month}`, { openedFrom: start, openedTo: end });
                        }
                      }}
                    />
                    <Bar dataKey="resolved" fill={alpha('#4caf50', 0.75)} radius={[4, 4, 0, 0]} name="resolved" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          ) : <Box />}
        </Box>

        {/* ── Row 3: Priority trend (full-width, conditional) ───────────── */}
        {trends?.priorityTrend && trends.priorityTrend.length > 0 && (
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: MI.forestMid }}>Priority trend</Typography>
                <Typography variant="caption" color="text.secondary" display="block">Last {windowDays} days · click a point for that priority & day</Typography>
              </Box>
              <Tooltip title="Incidents opened per day, split by priority label." arrow placement="left">
                <IconButton size="small"><InfoIcon fontSize="small" color="action" /></IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trends.priorityTrend} margin={{ top: 5, right: 20, left: 0, bottom: 55 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" angle={-45} textAnchor="end" height={65} tick={{ fontSize: 9 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11 }} width={32} />
                  <RechartsTooltip contentStyle={{ borderRadius: 8, fontSize: '0.8rem' }} />
                  <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '6px' }} />
                  {Object.keys(trends.priorityTrend[0] || {}).filter((k) => k !== 'date').map((priority, index) => (
                    <Line key={priority} type="monotone" dataKey={priority} stroke={COLORS[index % COLORS.length]} strokeWidth={2} name={priority}
                      dot={(props) => {
                        const { cx, cy, payload } = props;
                        if (cx == null || cy == null) return null;
                        return <circle cx={cx} cy={cy} r={3} fill={COLORS[index % COLORS.length]} stroke="#fff" strokeWidth={1} style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); handlePriorityTrendPointClick(priority, payload?.date); }} />;
                      }}
                      activeDot={{ r: 5 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        )}

        {/* ── Row 4: Service impact heatmap (full-width) ────────────────── */}
        {topServices.length > 0 && (
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.75 }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: MI.forestMid }}>Service impact heatmap</Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Incident volume per service · darker = higher impact · click to drill in
                </Typography>
              </Box>
              <Tooltip title="Total incident count per service. Colour intensity reflects relative volume — darker tiles need more attention." arrow placement="left">
                <IconButton size="small"><InfoIcon fontSize="small" color="action" /></IconButton>
              </Tooltip>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                '& > *': {
                  flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 calc(33.333% - 8px)', md: '1 1 calc(16.666% - 8px)' },
                  minWidth: { xs: 'calc(50% - 8px)', sm: 'calc(33.333% - 8px)', md: 'calc(16.666% - 8px)' },
                  maxWidth: { xs: 'calc(50% - 8px)', sm: 'calc(33.333% - 8px)', md: 'calc(16.666% - 8px)' }
                }
              }}
            >
              {(() => {
                const maxCount = Math.max(...topServices.map((s) => s.total), 1);
                const minCount = Math.min(...topServices.map((s) => s.total), 0);
                return topServices.map((svc) => {
                  const normalized = maxCount === minCount ? 0.5 : 0.1 + ((svc.total - minCount) / (maxCount - minCount)) * 0.9;
                  const bgOpacity = 0.15 + normalized * 0.75;
                  const textColor = normalized > 0.5 ? 'white' : MI.forest;
                  return (
                    <Box key={svc.service}>
                      <Tooltip title={`${svc.service}: ${svc.total} total · ${svc.active} open`} arrow>
                        <Box
                          onClick={() => openIncidentListModal(`Service: ${svc.service}`, { serviceTag: svc.service })}
                          sx={{
                            backgroundColor: `rgba(13, 148, 136, ${bgOpacity})`,
                            color: textColor,
                            border: `1px solid rgba(13, 148, 136, ${0.2 + normalized * 0.4})`,
                            p: 1.5,
                            borderRadius: 1.5,
                            textAlign: 'center',
                            cursor: 'pointer',
                            transition: 'transform 0.18s, box-shadow 0.18s',
                            '&:hover': { transform: 'scale(1.04)', boxShadow: 3 },
                            minHeight: 70,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.72rem', mb: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={svc.service}>
                            {svc.service}
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.3rem', lineHeight: 1 }}>
                            {svc.total}
                          </Typography>
                          {svc.active > 0 && (
                            <Typography variant="caption" sx={{ fontSize: '0.62rem', mt: 0.3, opacity: 0.9, fontWeight: 600 }}>
                              {svc.active} open
                            </Typography>
                          )}
                        </Box>
                      </Tooltip>
                    </Box>
                  );
                });
              })()}
            </Box>
          </Paper>
        )}

        {/* ── Rows 5-6: TTR charts + summary table (conditional) ────────── */}
        {timeToResolve?.byServiceTag && timeToResolve.byServiceTag.length > 0 && (
          <>
            {(timeToResolve.trend?.length > 0 || timeToResolve.byServiceTagTrend?.length > 0) && (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                {timeToResolve.trend && timeToResolve.trend.length > 0 && (
                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: MI.forestMid }}>Avg time to resolve trend</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">Click a point · lists incidents resolved that day</Typography>
                      </Box>
                      <Tooltip title="Daily average of (resolved − opened) duration for incidents resolved on that day." arrow placement="left">
                        <IconButton size="small"><InfoIcon fontSize="small" color="action" /></IconButton>
                      </Tooltip>
                    </Box>
                    <Box sx={{ height: 260 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={timeToResolve.trend} margin={{ top: 5, right: 20, left: 10, bottom: 55 }}>
                          <defs>
                            <linearGradient id="gradTtr" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={MI.forestMid} stopOpacity={0.2} />
                              <stop offset="95%" stopColor={MI.forestMid} stopOpacity={0.02} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="date" angle={-45} textAnchor="end" height={65} tick={{ fontSize: 9 }} interval="preserveStartEnd" />
                          <YAxis label={{ value: 'Hours', angle: -90, position: 'insideLeft', style: { fontSize: '10px' } }} tick={{ fontSize: 11 }} width={48} />
                          <RechartsTooltip formatter={(value) => [`${value} hrs`, 'Avg TTR']} contentStyle={{ borderRadius: 8, fontSize: '0.8rem' }} />
                          <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '6px' }} />
                          <Area type="monotone" dataKey="avgTimeToResolve" stroke={MI.forestMid} strokeWidth={2} fill="url(#gradTtr)" name="Avg (Hrs)"
                            dot={(props) => {
                              const { cx, cy, payload } = props;
                              if (cx == null || cy == null) return null;
                              return <circle cx={cx} cy={cy} r={4} fill={MI.forestMid} stroke="#fff" strokeWidth={1} style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); handleTtrTrendPointClick(payload?.date); }} />;
                            }}
                            activeDot={{ r: 6 }}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </Box>
                  </Paper>
                )}
                {timeToResolve.byServiceTagTrend && timeToResolve.byServiceTagTrend.length > 0 && (
                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: MI.forestMid }}>Time to resolve by service tag</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">Click a point · resolved that day for that tag</Typography>
                      </Box>
                      <Tooltip title="Each series is a service tag; values are averages for incidents resolved on that day." arrow placement="left">
                        <IconButton size="small"><InfoIcon fontSize="small" color="action" /></IconButton>
                      </Tooltip>
                    </Box>
                    <Box sx={{ height: 260 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={timeToResolve.byServiceTagTrend} margin={{ top: 5, right: 20, left: 10, bottom: 55 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="date" angle={-45} textAnchor="end" height={65} tick={{ fontSize: 9 }} interval="preserveStartEnd" />
                          <YAxis label={{ value: 'Hours', angle: -90, position: 'insideLeft', style: { fontSize: '10px' } }} tick={{ fontSize: 11 }} width={48} />
                          <RechartsTooltip formatter={(value) => [`${value} hrs`, '']} contentStyle={{ borderRadius: 8, fontSize: '0.8rem' }} />
                          <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '6px' }} />
                          {Object.keys(timeToResolve.byServiceTagTrend[0] || {}).filter((k) => k !== 'date').map((serviceTag, index) => (
                            <Line key={serviceTag} type="monotone" dataKey={serviceTag} stroke={COLORS[index % COLORS.length]} strokeWidth={2} name={serviceTag}
                              dot={(props) => {
                                const { cx, cy, payload } = props;
                                if (cx == null || cy == null) return null;
                                return <circle cx={cx} cy={cy} r={3} fill={COLORS[index % COLORS.length]} stroke="#fff" strokeWidth={1} style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); handleServiceTagTrendPointClick(serviceTag, payload?.date); }} />;
                              }}
                              activeDot={{ r: 5 }}
                            />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  </Paper>
                )}
              </Box>
            )}

            {/* TTR summary table */}
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: MI.forestMid, mb: 0.5 }}>Time to resolve summary by service tag</Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>Click a row to list incidents for that service / tag</Typography>
              <Box sx={{ overflowX: 'auto', borderRadius: 1, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                  <thead>
                    <tr style={{ background: `linear-gradient(135deg, ${MI.forestMid} 0%, #2d5a47 100%)`, color: 'white' }}>
                      {['Service Tag', 'Count', 'Avg (Hrs)', 'Median (Hrs)', 'Min (Hrs)', 'Max (Hrs)'].map((h) => (
                        <th key={h} style={{ padding: '12px 10px', textAlign: h === 'Service Tag' ? 'left' : 'right', fontSize: '0.8rem', fontWeight: 600 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {timeToResolve.byServiceTag.map((item, index) => (
                      <tr key={item.serviceTag} role="button" tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openIncidentListModal(`Service / tag: ${item.serviceTag}`, { serviceTag: item.serviceTag }); } }}
                        style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f8f9fa', transition: 'background-color 0.2s', cursor: 'pointer' }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e8f5e9'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#fff' : '#f8f9fa'; }}
                        onClick={() => openIncidentListModal(`Service / tag: ${item.serviceTag}`, { serviceTag: item.serviceTag })}
                      >
                        <td style={{ padding: '12px 10px', borderBottom: '1px solid #e0e0e0', fontWeight: 500 }}>{item.serviceTag}</td>
                        <td style={{ padding: '12px 10px', textAlign: 'right', borderBottom: '1px solid #e0e0e0' }}>{item.count}</td>
                        <td style={{ padding: '12px 10px', textAlign: 'right', borderBottom: '1px solid #e0e0e0', fontWeight: 600, color: MI.forestMid }}>{item.avgTimeToResolve}</td>
                        <td style={{ padding: '12px 10px', textAlign: 'right', borderBottom: '1px solid #e0e0e0' }}>{item.medianTimeToResolve}</td>
                        <td style={{ padding: '12px 10px', textAlign: 'right', borderBottom: '1px solid #e0e0e0' }}>{item.minTimeToResolve}</td>
                        <td style={{ padding: '12px 10px', textAlign: 'right', borderBottom: '1px solid #e0e0e0' }}>{item.maxTimeToResolve}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            </Paper>
          </>
        )}

        <Divider sx={{ my: 3 }} />

        {/* ─── All Incidents Table ───────────────────────────────────────── */}
        <Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: MI.forestMid }}>All Major Incidents</Typography>
              <Typography variant="caption" color="text.secondary">
                {tableLoading ? 'Loading…' : `${tableTotal.toLocaleString()} incident${tableTotal !== 1 ? 's' : ''} · search, filter, and export`}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() => exportToCsv(tableIncidents)}
              disabled={tableIncidents.length === 0}
              sx={{ borderColor: MI.forestMid, color: MI.forestMid, '&:hover': { bgcolor: alpha(MI.forest, 0.06) } }}
            >
              Export CSV
            </Button>
          </Stack>

          {/* Filter bar */}
          <Paper elevation={0} variant="outlined" sx={{ p: 1.5, mb: 1.5, borderRadius: 2 }}>
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  placeholder="Search number, description, problem…"
                  fullWidth
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={6} sm={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Priority</InputLabel>
                  <Select value={tablePriority} label="Priority" onChange={(e) => setTablePriority(e.target.value)}>
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="1 - Critical">P1 – Critical</MenuItem>
                    <MenuItem value="2 - High">P2 – High</MenuItem>
                    <MenuItem value="3 - Moderate">P3 – Moderate</MenuItem>
                    <MenuItem value="4 - Low">P4 – Low</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>State</InputLabel>
                  <Select value={tableState} label="State" onChange={(e) => setTableState(e.target.value)}>
                    <MenuItem value="">All states</MenuItem>
                    <MenuItem value="New">New</MenuItem>
                    <MenuItem value="In Progress">In Progress</MenuItem>
                    <MenuItem value="Assigned">Assigned</MenuItem>
                    <MenuItem value="Resolved">Resolved</MenuItem>
                    <MenuItem value="Closed">Closed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={2}>
                <Button
                  fullWidth
                  size="small"
                  variant={tableHasProblem ? 'contained' : 'outlined'}
                  startIcon={<AccountTreeIcon />}
                  onClick={() => setTableHasProblem((v) => !v)}
                  sx={{
                    height: 40,
                    ...(tableHasProblem
                      ? { bgcolor: '#7b1fa2', '&:hover': { bgcolor: '#6a1b9a' } }
                      : { borderColor: '#ce93d8', color: '#7b1fa2' })
                  }}
                >
                  Has Problem
                </Button>
              </Grid>
              <Grid item xs={6} sm={2}>
                <Button
                  fullWidth
                  size="small"
                  variant={tableHasRca ? 'contained' : 'outlined'}
                  startIcon={<AssignmentIcon />}
                  onClick={() => setTableHasRca((v) => !v)}
                  sx={{
                    height: 40,
                    ...(tableHasRca
                      ? { bgcolor: '#f57f17', '&:hover': { bgcolor: '#e65100' } }
                      : { borderColor: '#ffe082', color: '#f57f17' })
                  }}
                >
                  Has RCA
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Table */}
          <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            {tableLoading && <LinearProgress sx={{ height: 3 }} />}
            <TableContainer sx={{ maxHeight: 520 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {[
                      { id: 'number', label: 'Incident #', minWidth: 110 },
                      { id: 'shortDescription', label: 'Description', minWidth: 220 },
                      { id: 'state', label: 'State', minWidth: 100 },
                      { id: 'priority', label: 'Priority', minWidth: 100 },
                      { id: 'duration', label: 'Duration', minWidth: 80 },
                      { id: 'service', label: 'Service', minWidth: 100 },
                      { id: 'assignedTo', label: 'Assigned To', minWidth: 120 },
                      { id: 'openedAt', label: 'Opened', minWidth: 110 },
                      { id: 'problem', label: 'Problem', minWidth: 130 },
                      { id: 'rca', label: 'RCA', minWidth: 90 }
                    ].map((col) => (
                      <TableCell
                        key={col.id}
                        sx={{
                          fontWeight: 700,
                          bgcolor: MI.forestMid,
                          color: 'common.white',
                          fontSize: '0.8rem',
                          minWidth: col.minWidth,
                          whiteSpace: 'nowrap',
                          py: 1.25
                        }}
                      >
                        {col.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableIncidents.length === 0 && !tableLoading ? (
                    <TableRow>
                      <TableCell colSpan={10} sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>
                        No incidents match your filters
                      </TableCell>
                    </TableRow>
                  ) : (
                    tableIncidents.map((incident, idx) => {
                      const incidentUrl = getSnIncidentUrl(incident.sysId);
                      const problemUrl = getSnProblemUrl(incident.problemSysId);
                      const priorityStyle = getPriorityColor(incident.priority);
                      const stateStyle = getStateColor(incident.state);
                      const isResolved = RESOLVED_STATES.some((s) => (incident.state || '').toLowerCase().includes(s.toLowerCase()));

                      return (
                        <TableRow
                          key={incident._id || incident.sysId}
                          sx={{
                            '&:nth-of-type(odd)': { bgcolor: '#fafafa' },
                            '&:hover': { bgcolor: '#e8f5e9' },
                            transition: 'background-color 0.15s'
                          }}
                        >
                          {/* Incident # */}
                          <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                            {incidentUrl ? (
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Link href={incidentUrl} target="_blank" rel="noopener noreferrer"
                                  underline="hover" sx={{ color: '#1565c0', fontWeight: 600, fontSize: '0.8rem' }}>
                                  {incident.number || '—'}
                                </Link>
                                <OpenInNewIcon sx={{ fontSize: 12, color: 'text.secondary', opacity: 0.6 }} />
                              </Stack>
                            ) : (
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{incident.number || '—'}</Typography>
                            )}
                          </TableCell>

                          {/* Description */}
                          <TableCell sx={{ fontSize: '0.8rem', maxWidth: 260 }}>
                            <Tooltip title={incident.shortDescription || ''} arrow placement="top">
                              <Typography variant="body2" noWrap sx={{ fontSize: '0.8rem' }}>
                                {incident.shortDescription || '—'}
                              </Typography>
                            </Tooltip>
                          </TableCell>

                          {/* State */}
                          <TableCell>
                            <Chip
                              label={incident.state || '—'}
                              size="small"
                              sx={{
                                bgcolor: stateStyle.bg,
                                color: stateStyle.color,
                                fontWeight: 700,
                                fontSize: '0.68rem',
                                height: 22,
                                border: `1px solid ${alpha(stateStyle.color, 0.3)}`
                              }}
                            />
                          </TableCell>

                          {/* Priority */}
                          <TableCell>
                            <Chip
                              label={incident.priority || '—'}
                              size="small"
                              sx={{
                                bgcolor: priorityStyle.bg,
                                color: priorityStyle.color,
                                fontWeight: 700,
                                fontSize: '0.68rem',
                                height: 22,
                                border: `1px solid ${priorityStyle.border}`
                              }}
                            />
                          </TableCell>

                          {/* Duration */}
                          <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                            <Tooltip title={isResolved ? 'Time to resolve' : 'Time open (ongoing)'} arrow>
                              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: isResolved ? 'text.secondary' : '#e65100', fontWeight: isResolved ? 400 : 600 }}>
                                {fmtDuration(incident.openedAt, incident.resolvedAt)}
                              </Typography>
                            </Tooltip>
                          </TableCell>

                          {/* Service */}
                          <TableCell sx={{ fontSize: '0.8rem' }}>
                            <Typography variant="body2" noWrap sx={{ fontSize: '0.8rem', maxWidth: 120 }}
                              title={incident.service || incident.serviceTag || ''}>
                              {incident.service || incident.serviceTag || '—'}
                            </Typography>
                          </TableCell>

                          {/* Assigned To */}
                          <TableCell sx={{ fontSize: '0.8rem' }}>
                            <Typography variant="body2" noWrap sx={{ fontSize: '0.8rem', maxWidth: 130 }}>
                              {incident.assignedTo || 'Unassigned'}
                            </Typography>
                          </TableCell>

                          {/* Opened At */}
                          <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap', color: 'text.secondary' }}>
                            {formatDateShort(incident.openedAt)}
                          </TableCell>

                          {/* Problem */}
                          <TableCell sx={{ fontSize: '0.8rem', minWidth: 130 }}>
                            {incident.problemNumber || incident.problemSysId ? (
                              <Box>
                                {problemUrl ? (
                                  <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <Link href={problemUrl} target="_blank" rel="noopener noreferrer"
                                      underline="hover" sx={{ color: '#7b1fa2', fontWeight: 600, fontSize: '0.8rem' }}>
                                      {incident.problemNumber || 'PRB'}
                                    </Link>
                                    <OpenInNewIcon sx={{ fontSize: 11, color: '#7b1fa2', opacity: 0.7 }} />
                                  </Stack>
                                ) : (
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#7b1fa2', fontSize: '0.8rem' }}>
                                    {incident.problemNumber || incident.problemSysId}
                                  </Typography>
                                )}
                                {incident.problemShortDescription && (
                                  <Tooltip title={incident.problemShortDescription} arrow>
                                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', fontSize: '0.65rem', maxWidth: 120 }}>
                                      {incident.problemShortDescription}
                                    </Typography>
                                  </Tooltip>
                                )}
                                {incident.problemState && (
                                  <Chip label={incident.problemState} size="small" sx={{ mt: 0.25, height: 18, fontSize: '0.6rem' }} />
                                )}
                              </Box>
                            ) : (
                              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.disabled' }}>—</Typography>
                            )}
                          </TableCell>

                          {/* RCA download */}
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {incident.incidentReportAttachmentSysId ? (
                              <Tooltip title="Download Incident Report" arrow>
                                <IconButton
                                  size="small"
                                  disabled={downloadingReportSysId === incident.sysId}
                                  onClick={() => handleDownloadRca(incident, false)}
                                  sx={{ color: '#f57f17' }}
                                >
                                  {downloadingReportSysId === incident.sysId
                                    ? <CircularProgress size={16} color="inherit" />
                                    : <DownloadIcon sx={{ fontSize: 18 }} />}
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="No incident report attached in ServiceNow. Click to attempt download anyway." arrow>
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={!incident.sysId || downloadingReportSysId === incident.sysId}
                                    onClick={() => handleDownloadRca(incident, false)}
                                    sx={{ color: 'text.disabled' }}
                                  >
                                    {downloadingReportSysId === incident.sysId
                                      ? <CircularProgress size={16} color="inherit" />
                                      : <DownloadIcon sx={{ fontSize: 18 }} />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={tableTotal}
              page={tablePage}
              rowsPerPage={tableRowsPerPage}
              rowsPerPageOptions={TABLE_ROWS_OPTIONS}
              onPageChange={(e, newPage) => setTablePage(newPage)}
              onRowsPerPageChange={(e) => { setTableRowsPerPage(parseInt(e.target.value, 10)); setTablePage(0); }}
              sx={{ borderTop: '1px solid #e0e0e0' }}
            />
          </Paper>
        </Box>

      {/* ─── Chart drill-down modal ────────────────────────────────────── */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{ background: `linear-gradient(135deg, ${MI.forestMid} 0%, #2d5a47 100%)`, color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {modalTitle} ({modalIncidents.length})
          </Typography>
          <IconButton onClick={handleCloseModal} sx={{ color: 'white' }} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, mt: 2 }}>
          {modalNotice && (
            <Alert severity="warning" sx={{ mx: 2, mb: 1 }} onClose={() => setModalNotice(null)}>{modalNotice}</Alert>
          )}
          {modalLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px"><CircularProgress /></Box>
          ) : modalIncidents.length === 0 ? (
            <Box p={3} textAlign="center">
              <Typography variant="body2" color="text.secondary">No incidents found</Typography>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: '60vh' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {['Incident #', 'Short Description', 'State', 'Priority', 'Assigned To', 'Opened At', 'Resolved At', 'Service', 'Problem', 'Incident Report'].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5', fontSize: '0.875rem', borderBottom: `2px solid ${MI.forestMid}`, whiteSpace: 'nowrap' }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {modalIncidents.map((incident) => {
                    const incidentUrl = getSnIncidentUrl(incident.sysId);
                    const problemUrl = getSnProblemUrl(incident.problemSysId);
                    return (
                      <TableRow key={incident._id || incident.sysId} sx={{ '&:nth-of-type(odd)': { bgcolor: '#fafafa' }, '&:hover': { bgcolor: '#e8f5e9' } }}>
                        <TableCell sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                          {incidentUrl ? (
                            <Typography component="a" href={incidentUrl} target="_blank" rel="noopener noreferrer"
                              sx={{ color: '#1976d2', textDecoration: 'none', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}>
                              {incident.number || 'N/A'}
                            </Typography>
                          ) : (incident.number || 'N/A')}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.875rem', maxWidth: 300 }}>
                          <Typography variant="body2" noWrap title={incident.shortDescription}>{incident.shortDescription || 'N/A'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={incident.state || 'N/A'} size="small"
                            sx={{ bgcolor: getStateColor(incident.state).bg, color: getStateColor(incident.state).color, fontWeight: 'bold', fontSize: '0.7rem' }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.875rem' }}>{incident.priority || 'N/A'}</TableCell>
                        <TableCell sx={{ fontSize: '0.875rem' }}>{incident.assignedTo || 'Unassigned'}</TableCell>
                        <TableCell sx={{ fontSize: '0.875rem' }}>{formatDate(incident.openedAt)}</TableCell>
                        <TableCell sx={{ fontSize: '0.875rem' }}>{formatDate(incident.resolvedAt)}</TableCell>
                        <TableCell sx={{ fontSize: '0.875rem' }}>{incident.service || incident.serviceTag || 'N/A'}</TableCell>
                        <TableCell sx={{ fontSize: '0.875rem', maxWidth: 220 }}>
                          {incident.problemNumber || incident.problemSysId ? (
                            <Box>
                              {problemUrl ? (
                                <Link href={problemUrl} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ fontWeight: 600, display: 'block' }}>
                                  {incident.problemNumber || 'Open problem'}
                                </Link>
                              ) : (
                                <Typography variant="body2" fontWeight={600}>{incident.problemNumber || incident.problemSysId}</Typography>
                              )}
                              {incident.problemShortDescription && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }} noWrap title={incident.problemShortDescription}>
                                  {incident.problemShortDescription}
                                </Typography>
                              )}
                              {incident.problemState && <Chip label={incident.problemState} size="small" sx={{ mt: 0.5, height: 20, fontSize: '0.65rem' }} />}
                            </Box>
                          ) : '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                          <Button type="button" size="small" variant="outlined" color="primary"
                            startIcon={downloadingReportSysId === incident.sysId ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon sx={{ fontSize: 18 }} />}
                            disabled={!incident.sysId || downloadingReportSysId === incident.sysId}
                            onClick={() => handleDownloadRca(incident, true)}
                          >
                            Download
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={handleCloseModal} variant="contained" sx={{ bgcolor: MI.forestMid }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IncidentTab;
